import { ComponentPropsWithoutRef, useContext } from 'react';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { faArrowLeft, faArrowRight, faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableContext } from 'components/table/TableContext';
import {
    PAGINATION_SLICE,
    useReactTablePagination,
} from 'components/table/useReactTablePagination';
import { twMerge } from 'tailwind-merge';

interface TablePageNavigationProps extends ComponentPropsWithoutRef<'div'> {
    sliceCount?: number;
}

const TablePageNavigation = (props: TablePageNavigationProps) => {
    const { table } = useContext(TableContext);
    const { sliceCount = PAGINATION_SLICE, className = '' } = props;
    const {
        showSecondFromLeft,
        showSecondFromRight,
        prevButtonProps,
        nextButtonProps,
        firstPageButtonProps,
        secondPageButtonProps,
        dynamicPageButtonProps,
        secondToLastPageButtonProps,
        lastPageButtonProps,
    } = useReactTablePagination(table, sliceCount);

    return (
        <div className={twMerge(`flex items-center`, className)}>
            <PaginationButton
                onClick={prevButtonProps.onClick}
                disabled={prevButtonProps.disabled}
                className={'flex items-center gap-x-2 rounded-l-lg border-l border-r-0 sm:border-r'}
            >
                <FontAwesomeIcon size={'sm'} icon={faArrowLeft} />
                <p className={'hidden md:block'}>Previous</p>
            </PaginationButton>
            <PaginationPageNumberButton
                className={selectedStyle(firstPageButtonProps.selected)}
                onClick={firstPageButtonProps.onClick}
            >
                {firstPageButtonProps.showValue}
            </PaginationPageNumberButton>
            {secondPageButtonProps.show && (
                <PaginationPageNumberButton
                    className={selectedStyle(secondPageButtonProps.selected)}
                    onClick={secondPageButtonProps.onClick}
                    disabled={!showSecondFromLeft}
                >
                    {showSecondFromLeft ? (
                        secondPageButtonProps.showValue
                    ) : (
                        <>
                            <p className={'sr-only'}>{secondPageButtonProps.showValue}</p>
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </>
                    )}
                </PaginationPageNumberButton>
            )}
            {dynamicPageButtonProps.map((props, i) => {
                return (
                    props.show && (
                        <PaginationPageNumberButton
                            key={`pagination-sibling-${props.showValue}-${i}`}
                            className={selectedStyle(props.selected)}
                            onClick={props.onClick}
                        >
                            {props.showValue}
                        </PaginationPageNumberButton>
                    )
                );
            })}
            <div
                className={
                    'flex h-[34px] items-center justify-center border-y border-gray-300 bg-white px-2 sm:hidden'
                }
            >
                <p className={'whitespace-nowrap text-sm font-medium'}>
                    Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </p>
            </div>
            {secondToLastPageButtonProps.show && (
                <PaginationPageNumberButton
                    onClick={secondToLastPageButtonProps.onClick}
                    className={selectedStyle(secondToLastPageButtonProps.selected)}
                    disabled={!showSecondFromRight}
                >
                    {showSecondFromRight ? (
                        secondToLastPageButtonProps.showValue
                    ) : (
                        <>
                            <p className={'sr-only'}>{secondToLastPageButtonProps.showValue}</p>
                            <FontAwesomeIcon icon={faEllipsisV} />
                        </>
                    )}
                </PaginationPageNumberButton>
            )}
            {lastPageButtonProps.show && (
                <PaginationPageNumberButton
                    onClick={lastPageButtonProps.onClick}
                    className={selectedStyle(lastPageButtonProps.selected)}
                >
                    {lastPageButtonProps.showValue}
                </PaginationPageNumberButton>
            )}
            <PaginationButton
                onClick={nextButtonProps.onClick}
                disabled={nextButtonProps.disabled}
                className={'flex items-center gap-x-2 rounded-r-lg'}
            >
                <p className={'hidden md:block'}>Next</p>
                <FontAwesomeIcon size={'sm'} icon={faArrowRight} />
            </PaginationButton>
        </div>
    );
};

interface PaginationButtonProps extends Omit<ButtonProps, 'variant'> {}

const PaginationButton = (props: PaginationButtonProps) => {
    const { className = '', ...rest } = props;

    return (
        <Button
            variant="none"
            color="neutral"
            size="sm"
            className={twMerge(
                'h-8 border-y border-r border-gray-300 p-4 font-medium text-gray-700 hover:bg-gray-50',
                className,
            )}
            {...rest}
        />
    );
};

interface PaginationPageNumberButtonProps extends Omit<ButtonProps, 'variant'> {}

const PaginationPageNumberButton = (props: PaginationPageNumberButtonProps) => {
    const { className = '', ...rest } = props;

    return (
        <PaginationButton
            className={twMerge('hidden items-center justify-center px-3.5 sm:flex', className)}
            {...rest}
        />
    );
};

const selectedStyle = (cond: boolean) => (cond ? '!bg-blue-50 !text-blue-500' : '');

export { TablePageNavigation };
export type { TablePageNavigationProps };
